import { useState } from "react";
import { useNavigate } from "react-router-dom";

const PD = "'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, sans-serif";

type Role = "student" | "instructor";

const studentSteps = [
  {
    step: "01",
    title: "회원가입 & 로그인",
    icon: "ri-user-add-line",
    color: "#4B6CF7",
    description: "K-ArtPEN에 접속하여 이메일로 회원가입하거나, 강사님이 보내신 초대 이메일을 통해 워크스페이스에 바로 입장할 수 있습니다.",
    details: [
      "이메일 주소와 비밀번호로 계정 생성 후 로그인",
      "강사 초대 이메일 수신 시 링크 클릭 → 워크스페이스 자동 배정",
      "이미 계정이 있다면 로그인 후 워크스페이스에 합류",
      "로그인 후 내 프로필(닉네임)을 설정하면 팀원에게 표시됩니다",
    ],
    tip: "강사님이 발송한 초대 이메일을 먼저 확인하세요. 초대 링크로 입장하면 워크스페이스가 자동으로 연결됩니다.",
  },
  {
    step: "02",
    title: "기업 유형 선택",
    icon: "ri-building-4-line",
    color: "#7C6FF7",
    description: "시뮬레이션할 문화예술 기업 유형을 선택하세요. 선택한 유형에 따라 업무 화면, 직무 목록, KPI 지표가 완전히 달라집니다.",
    details: [
      "뮤직 레이블 — 음원 유통·아티스트 계약·앨범 제작 중심",
      "공연기획사 — 콘서트 기획·티켓 판매·스폰서 유치 중심",
      "문화재단 — 보조금 수주·사업 집행·후원자 관리 (비영리)",
      "웹툰 / IP 스튜디오 — 연재 기획·IP 라이선싱·해외 판권 수출",
      "광고·콘텐츠 에이전시 — 광고 캠페인 수주·MV 제작·데이터 분석",
    ],
    tip: "처음이라면 '뮤직 레이블'을 추천합니다. 5개 직무 역할이 가장 균형 있게 구성되어 있어요.",
  },
  {
    step: "03",
    title: "직무 역할 선택",
    icon: "ri-briefcase-line",
    color: "#6B9FF7",
    description: "선택한 기업 유형에 맞는 직무를 선택하세요. 직무마다 접근 가능한 메뉴와 업무가 다르며, 팀 내 협업이 필수입니다.",
    details: [
      "대표이사/사무국장 — 전사 현황 대시보드, 최종 결재권 행사, 전 메뉴 접근",
      "A&R / 에디터 / 크리에이티브 디렉터 — 창작 허브(A&R Hub, IP Studio, 캠페인 허브) 전담",
      "재무 / 회계 담당 — 재무 ERP 전담 (전표처리·예산 관리·세금 신고)",
      "마케팅 담당 — 마케팅 워룸 (SNS·보도자료·캠페인·차트 분석)",
      "법무 / 계약 담당 — 법무&IP 메뉴 (계약서 검토·독소조항 탐지·저작권 등록)",
    ],
    tip: "팀 프로젝트라면 반드시 각자 다른 직무를 선택하세요. 재무 결재는 재무 담당이, 계약 검토는 법무 담당이 처리해야 워크플로우가 작동합니다.",
  },
  {
    step: "04",
    title: "대시보드 파악 & 업무 시작",
    icon: "ri-dashboard-3-line",
    color: "#5BB5F7",
    description: "대시보드에서 현재 회사 상황을 파악하고, 랜덤 이벤트에 대응하며 할 일 목록을 처리하세요. 모든 행동이 회사 수치에 반영됩니다.",
    details: [
      "대시보드 — 자본금·신용도·명성 지수·프로젝트 현황 한눈에 확인",
      "인박스 — 이해관계자(투자자·언론사·유통사 등) 협상 메일 수신·회신",
      "전자결재 — 계약서·예산 기안서 기안 및 승인/반려 처리",
      "직무별 허브 — A&R Hub / 공연기획 / 사업관리 / IP Studio / 캠페인 허브",
      "재무 ERP — 전표처리·계좌 잔액·세금 이벤트 대응",
    ],
    tip: "랜덤 이벤트 팝업이 뜨면 즉시 팀원과 상의하세요. 3가지 선택지마다 자본금과 명성에 미치는 영향이 다릅니다.",
  },
  {
    step: "05",
    title: "결재 협업 & 이벤트 대응",
    icon: "ri-team-line",
    color: "#4BBFF7",
    description: "전자결재 라인을 통해 팀 내 의사결정이 이루어집니다. 기안 → 검토 → 최종 결재 흐름이 실제 기업과 동일하게 작동합니다.",
    details: [
      "계약서 기안 (A&R·법무) → 재무 검토 → 대표 최종 결재",
      "예산 기안 (해당 직무) → 재무 담당 심사 → 대표 승인",
      "강사님이 발송하는 랜덤 이벤트에 팀 전체가 대응",
      "결재 반려 시 수정 후 재기안 — 실제 업무 흐름과 동일",
      "이벤트 결과에 따라 자본금·신용도·명성 지수가 즉시 변동",
    ],
    tip: "모든 행동이 강사님 어드민에 기록됩니다. 결재를 빠르게 처리할수록 이벤트 대응 여유가 생겨요!",
  },
];

const instructorSteps = [
  {
    step: "01",
    title: "강사 계정 & 어드민 접근",
    icon: "ri-user-star-line",
    color: "#F7784B",
    description: "강사 계정으로 로그인하면 일반 수강생에게는 보이지 않는 어드민 패널에 접근할 수 있습니다. 강사 계정은 별도 요청으로 발급됩니다.",
    details: [
      "로그인 후 좌측 사이드바 최하단 '어드민 패널' 메뉴 클릭",
      "God Mode 대시보드: 전체 워크스페이스 자본금·KPI 실시간 열람",
      "이벤트 디스패처: 수강생 전체 또는 특정 팀에 랜덤 이벤트 수동 발송",
      "강사 계정은 수강생 화면에서 보이지 않으며 별도 어드민 전용 화면 제공",
    ],
    tip: "처음 강사 계정 발급이 필요하시면 eunhyun1@naver.com 으로 문의 주세요.",
  },
  {
    step: "02",
    title: "수강생 초대 & 워크스페이스 설정",
    icon: "ri-group-line",
    color: "#F7A84B",
    description: "어드민 패널 내 초대 관리에서 수강생 이메일을 입력해 초대장을 발송하세요. 수강생은 이메일 링크를 클릭하면 자동으로 워크스페이스에 합류합니다.",
    details: [
      "어드민 패널 → '초대 관리' → 이메일 주소 입력 후 초대 발송",
      "초대받은 수강생은 이메일 링크 클릭 → 회원가입/로그인 → 자동 배정",
      "워크스페이스당 최대 20명까지 지원 (5가지 기업 유형 × 5직무 × 4팀 구성 가능)",
      "수강생 배정 현황은 어드민 패널 → '수강생 관리'에서 실시간 확인",
    ],
    tip: "수업 전에 미리 수강생 이메일 목록을 준비해두면 초대가 훨씬 빠릅니다.",
  },
  {
    step: "03",
    title: "기업 유형 & 시나리오 구성",
    icon: "ri-settings-4-line",
    color: "#F7C84B",
    description: "수업 목표에 맞는 기업 유형을 수강생이 선택하도록 안내하거나, 강사가 직접 사전 지정할 수 있습니다. 수업 난이도는 초기 자본금으로 조절합니다.",
    details: [
      "5가지 기업 유형 중 수업 목적에 맞게 선택 또는 자유 선택 허용",
      "기업 유형별로 활성화되는 메뉴와 KPI 지표가 자동 변경됨",
      "초기 자본금 조정: 낮을수록 재무 압박이 커져 난이도 상승",
      "비영리(문화재단) 선택 시 보조금 기반 회계 구조로 전환",
      "각 기업별 전용 허브(A&R Hub, 공연기획, IP Studio 등) 자동 활성화",
    ],
    tip: "기업 유형을 통일하면 팀 간 비교가 쉬워지고, 다양하게 섞으면 유형별 차이를 토론할 수 있어요.",
  },
  {
    step: "04",
    title: "이벤트 디스패처로 수업 진행",
    icon: "ri-send-plane-line",
    color: "#4BF7A8",
    description: "어드민 전용 이벤트 디스패처에서 12가지 랜덤 이벤트를 수동 발송할 수 있습니다. 각 이벤트에는 3가지 선택지가 있어 팀별 의사결정이 달라집니다.",
    details: [
      "어드민 패널 → '이벤트 디스패처' → 이벤트 선택 → 발송 대상 선택",
      "위기 이벤트(⚠️): SNS 논란·표절 시비·세무조사·음원 유출 등 6가지",
      "기회 이벤트(🚀): 바이럴 급상승·K-POP 페스티벌 섭외·VC 투자 제안 등 6가지",
      "이벤트 발송 후 수강생 선택 결과가 자본금·명성에 즉시 반영됨",
      "같은 이벤트를 여러 팀에 동시 발송해 결과를 비교하는 토론 수업 활용 가능",
    ],
    tip: "이벤트는 수강생이 기본 업무(결재·메일 처리)를 충분히 경험한 뒤 발송하면 반응이 더 풍부합니다.",
  },
  {
    step: "05",
    title: "God Mode 모니터링 & 피드백",
    icon: "ri-bar-chart-grouped-line",
    color: "#4BF7C8",
    description: "God Mode 대시보드에서 모든 워크스페이스의 자본금·KPI·결재 현황을 실시간으로 한눈에 파악하고, 팀별 피드백을 제공하세요.",
    details: [
      "어드민 → God Mode: 전체 팀 자본금 순위·명성 지수·신용도 비교 조회",
      "수강생별 전자결재 기안 내역, 인박스 활동 로그 열람 가능",
      "이벤트 선택 결과별 팀 성과 분기점 확인 → 수업 토론 주제로 활용",
      "특정 팀의 자본금이 위험 수준이면 강사 개입 이벤트 발송으로 학습 유도",
      "수업 종료 후 전체 팀 결과를 비교해 경영 의사결정 토론 진행",
    ],
    tip: "수업 중간에 자본금 1등 팀의 이벤트 선택 결과를 전체 공유하면 학습 몰입도가 크게 높아집니다.",
  },
];

const faqs = [
  {
    q: "K-ArtPEN은 어떤 디바이스에서 사용할 수 있나요?",
    a: "현재 PC 웹 브라우저(Chrome, Edge, Firefox)에 최적화되어 있습니다. 태블릿 및 모바일은 추후 지원 예정입니다. 화면 해상도 1280px 이상을 권장합니다."
  },
  {
    q: "기업 유형은 나중에 바꿀 수 있나요?",
    a: "기업 유형을 변경하면 모든 업무 기록과 자본금이 초기화됩니다. 수업 중 변경은 강사 어드민에서만 처리 가능하며, 일반적으로 수업 시작 전에 확정하는 것을 권장합니다."
  },
  {
    q: "랜덤 이벤트는 자동으로 발생하나요?",
    a: "아니요. 랜덤 이벤트는 강사님이 어드민 이벤트 디스패처에서 수동으로 발송합니다. 총 12가지 이벤트(위기 6개, 기회 6개)가 있으며, 각 이벤트마다 3가지 선택지가 제공됩니다. 선택 결과에 따라 자본금·명성·신용도에 차등 영향이 반영됩니다."
  },
  {
    q: "전자결재 승인은 누가 하나요?",
    a: "기안서의 종류에 따라 결재 라인이 다릅니다. 계약서·예산 기안은 재무 담당 검토 → 대표이사 최종 결재 흐름으로 진행됩니다. 대표 직무를 선택한 팀원이 없으면 결재가 보류되므로, 팀 내 반드시 대표 역할이 있어야 합니다."
  },
  {
    q: "한 수업에 몇 명까지 참여할 수 있나요?",
    a: "워크스페이스당 최대 20명까지 지원합니다. 5가지 기업 유형 × 각 5개 직무 구조이므로, 최대 4개 팀(20명)이 서로 다른 기업 유형으로 시뮬레이션을 진행할 수 있습니다. 대규모 수업은 별도 문의 부탁드립니다."
  },
  {
    q: "강사 계정과 수강생 계정의 차이는 무엇인가요?",
    a: "강사 계정은 좌측 사이드바에 '어드민 패널' 메뉴가 추가로 표시되며, God Mode 대시보드(전체 팀 현황 열람)·이벤트 디스패처·수강생 초대 관리 기능을 사용할 수 있습니다. 수강생 계정은 자신의 직무 화면과 업무 도구만 이용할 수 있습니다."
  },
];

type CompanyKey = "MusicLabel" | "LiveEntertainment" | "CulturalFoundation" | "WebtoonStudio" | "ContentAgency";

const companyExplorer: {
  key: CompanyKey;
  name: string;
  en: string;
  icon: string;
  color: string;
  capital: string;
  hubMenu: string;
  roles: { label: string; icon: string; desc: string; menus: string[] }[];
}[] = [
  {
    key: "MusicLabel",
    name: "뮤직 레이블",
    en: "Music Label",
    icon: "ri-music-2-line",
    color: "#4B6CF7",
    capital: "5,000만원",
    hubMenu: "A&R Hub",
    roles: [
      { label: "CEO / 대표이사", icon: "ri-vip-crown-line", desc: "전사 현황 모니터링·최종 계약 결재·투자자 미팅", menus: ["대시보드", "인박스", "전자결재", "A&R Hub", "재무 ERP", "마케팅 워룸", "법무&IP"] },
      { label: "A&R 담당", icon: "ri-music-2-line", desc: "아티스트 발굴·앨범 기획서·데모곡 평가·무드보드 구성", menus: ["대시보드", "인박스", "A&R Hub", "전자결재"] },
      { label: "재무 담당", icon: "ri-bank-line", desc: "예산안 심사·가상 계좌 관리·세금 납부·손익 보고", menus: ["대시보드", "인박스", "재무 ERP", "전자결재"] },
      { label: "마케팅 담당", icon: "ri-megaphone-line", desc: "SNS 콘텐츠 기획·보도자료 배포·음원 차트 분석·위기 대응", menus: ["대시보드", "인박스", "마케팅 워룸"] },
      { label: "법무 담당", icon: "ri-scales-3-line", desc: "계약서 독소조항 검토·저작권 등록·내용증명 대응", menus: ["대시보드", "인박스", "법무&IP", "전자결재"] },
    ],
  },
  {
    key: "LiveEntertainment",
    name: "공연기획사",
    en: "Live Entertainment",
    icon: "ri-mic-line",
    color: "#7A9BFF",
    capital: "5,000만원",
    hubMenu: "공연기획 허브",
    roles: [
      { label: "대표이사", icon: "ri-vip-crown-line", desc: "아티스트 라인업 확정·스폰서 계약 결재·공연 손익 검토", menus: ["대시보드", "인박스", "공연기획", "전자결재", "재무 ERP", "마케팅 워룸"] },
      { label: "공연기획 PD", icon: "ri-mic-line", desc: "공연 기획안 작성·아티스트 섭외·세트리스트 구성", menus: ["대시보드", "인박스", "공연기획", "전자결재"] },
      { label: "티켓영업 MD", icon: "ri-ticket-line", desc: "티켓 가격 전략·판매 채널 관리·얼리버드 프로모션", menus: ["대시보드", "인박스", "공연기획"] },
      { label: "마케팅팀장", icon: "ri-megaphone-line", desc: "공연 홍보물 기획·SNS 캠페인·보도자료 배포", menus: ["대시보드", "인박스", "마케팅 워룸"] },
      { label: "재무담당", icon: "ri-bank-line", desc: "공연 예산 관리·스폰서 수익 정산·세금 신고", menus: ["대시보드", "인박스", "재무 ERP", "전자결재"] },
    ],
  },
  {
    key: "CulturalFoundation",
    name: "문화재단",
    en: "Cultural Foundation",
    icon: "ri-community-line",
    color: "#9BB5FF",
    capital: "정부 보조금 기반",
    hubMenu: "사업관리 허브",
    roles: [
      { label: "사무국장", icon: "ri-vip-crown-line", desc: "이사회 보고·보조금 신청 결재·외부 기관 협력", menus: ["대시보드", "인박스", "사업관리", "전자결재", "재무 ERP"] },
      { label: "사업팀장", icon: "ri-community-line", desc: "문화예술 지원사업 기획·수혜자 선정 심사·결과 보고", menus: ["대시보드", "인박스", "사업관리", "전자결재"] },
      { label: "후원개발팀", icon: "ri-heart-line", desc: "기업 후원 제안서·후원자 CRM·기부금 캠페인 운영", menus: ["대시보드", "인박스", "사업관리"] },
      { label: "회계담당", icon: "ri-bank-line", desc: "보조금 수입·지출 관리·비영리 전표처리·외부 감사 대응", menus: ["대시보드", "인박스", "재무 ERP", "전자결재"] },
      { label: "사업평가위원", icon: "ri-star-line", desc: "사업 성과 지표 설계·수혜자 인터뷰·임팩트 보고서", menus: ["대시보드", "인박스", "사업관리"] },
    ],
  },
  {
    key: "WebtoonStudio",
    name: "웹툰 / IP 스튜디오",
    en: "Webtoon & IP Studio",
    icon: "ri-quill-pen-line",
    color: "#AACCFF",
    capital: "5,000만원",
    hubMenu: "IP 스튜디오",
    roles: [
      { label: "대표이사", icon: "ri-vip-crown-line", desc: "플랫폼 계약 결재·IP 전략 결정·해외 판권 협상", menus: ["대시보드", "인박스", "IP 스튜디오", "전자결재", "법무&IP", "마케팅 워룸"] },
      { label: "에디터", icon: "ri-quill-pen-line", desc: "작가 발굴 및 계약·연재 스케줄 관리·원고 편집 피드백", menus: ["대시보드", "인박스", "IP 스튜디오"] },
      { label: "계약 MD", icon: "ri-file-paper-2-line", desc: "IP 라이선싱 계약·드라마 판권 협상·굿즈 파트너 계약", menus: ["대시보드", "인박스", "IP 스튜디오", "전자결재"] },
      { label: "마케터", icon: "ri-megaphone-line", desc: "플랫폼 추천 노출 협상·SNS 팬덤 운영·IP 브랜딩", menus: ["대시보드", "인박스", "마케팅 워룸"] },
      { label: "법무담당", icon: "ri-scales-3-line", desc: "저작권 등록·표절 시비 대응·플랫폼 계약 검토", menus: ["대시보드", "인박스", "법무&IP", "전자결재"] },
    ],
  },
  {
    key: "ContentAgency",
    name: "광고·콘텐츠 에이전시",
    en: "Content & Ad Agency",
    icon: "ri-video-line",
    color: "#BBDDFF",
    capital: "5,000만원",
    hubMenu: "캠페인 허브",
    roles: [
      { label: "대표이사", icon: "ri-vip-crown-line", desc: "주요 캠페인 수주 결재·클라이언트 임원 미팅·P&L 검토", menus: ["대시보드", "인박스", "캠페인 허브", "전자결재", "재무 ERP", "마케팅 워룸"] },
      { label: "크리에이티브 디렉터", icon: "ri-paint-brush-line", desc: "크리에이티브 브리프 작성·비주얼 컨셉 개발·광고 심의 대응", menus: ["대시보드", "인박스", "캠페인 허브"] },
      { label: "영업 AE", icon: "ri-briefcase-line", desc: "클라이언트 제안서 작성·프레젠테이션 발표·계약 협상", menus: ["대시보드", "인박스", "캠페인 허브", "전자결재"] },
      { label: "영상 PD", icon: "ri-video-line", desc: "MV·광고 영상 기획·촬영 스케줄·외주 스태프 계약", menus: ["대시보드", "인박스", "캠페인 허브"] },
      { label: "데이터 분석가", icon: "ri-bar-chart-line", desc: "캠페인 성과 리포트·CPM·CPC·전환율 최적화·A/B 테스트", menus: ["대시보드", "인박스", "마케팅 워룸"] },
    ],
  },
];

export default function GuidePage() {
  const navigate = useNavigate();
  const [role, setRole] = useState<Role>("student");
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activeCompany, setActiveCompany] = useState(0);

  const steps = role === "student" ? studentSteps : instructorSteps;

  return (
    <div className="min-h-screen bg-[#0C0C0E]" style={{ fontFamily: PD }}>
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 border-b border-white/[0.06] bg-[#0C0C0E]/90 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#888888] hover:text-[#CCCCCC] text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            K-ArtPEN 홈
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center rounded-md bg-[#4B6CF7]">
              <span className="text-white font-bold text-xs">K</span>
            </div>
            <span className="text-[#F2F2F2] font-semibold text-sm">이용 가이드</span>
          </div>
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="text-sm text-[#4B6CF7] hover:text-[#7A9BFF] transition-colors cursor-pointer whitespace-nowrap"
          >
            시작하기 →
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 text-center">
        <div className="inline-flex items-center gap-2 text-[#7A9BFF] text-xs uppercase tracking-widest mb-5">
          <i className="ri-book-open-line"></i> Platform Guide
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-[#F2F2F2] mb-5 leading-snug">
          K-ArtPEN 시작 가이드
        </h1>
        <p className="text-[#777777] text-base md:text-lg max-w-xl mx-auto leading-relaxed mb-10">
          강사와 수강생 모두를 위한 단계별 안내입니다.
          <br />처음 접속부터 시뮬레이션 시작까지 함께 알아볼게요.
        </p>

        {/* 5×5 Matrix Table */}
        <div className="w-full max-w-5xl mx-auto mt-2 mb-10 px-6">
          <div className="bg-[#111112] border border-white/[0.07] rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-white/[0.05] flex items-center gap-2">
              <i className="ri-table-line text-[#7A9BFF] text-sm"></i>
              <span className="text-[#CCCCCC] text-sm font-semibold">기업 유형별 직무 역할 한눈에 보기</span>
              <span className="ml-auto text-[#444444] text-xs">5가지 기업 × 5가지 직무 = 전체 16가지 고유 역할</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[700px]">
                <thead>
                  <tr className="border-b border-white/[0.05]">
                    <td className="py-3 px-4 text-[#3D3D3D] text-[10px] uppercase tracking-widest w-24">직무 번호</td>
                    {[
                      { name: "뮤직 레이블", en: "Music Label", icon: "ri-music-2-line", color: "#4B6CF7" },
                      { name: "공연기획사", en: "Live Entertainment", icon: "ri-mic-line", color: "#7A9BFF" },
                      { name: "문화재단", en: "Cultural Foundation", icon: "ri-community-line", color: "#9BB5FF" },
                      { name: "웹툰·IP 스튜디오", en: "Webtoon & IP Studio", icon: "ri-quill-pen-line", color: "#AACCFF" },
                      { name: "광고·콘텐츠 에이전시", en: "Content & Ad Agency", icon: "ri-video-line", color: "#BBDDFF" },
                    ].map((co) => (
                      <th key={co.en} className="py-3 px-3 text-left font-medium">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <i className={`${co.icon} text-xs`} style={{ color: co.color }}></i>
                          <span className="text-[#CCCCCC] text-[11px] whitespace-nowrap">{co.name}</span>
                        </div>
                        <span className="text-[#3D3D3D] text-[9px] uppercase tracking-wider">{co.en}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      num: "① 총괄",
                      roles: [
                        { label: "CEO / 대표이사", tag: "공통", color: "#4B6CF7" },
                        { label: "대표이사", tag: "공통", color: "#4B6CF7" },
                        { label: "사무국장", tag: "공통", color: "#4B6CF7" },
                        { label: "대표이사", tag: "공통", color: "#4B6CF7" },
                        { label: "대표이사", tag: "공통", color: "#4B6CF7" },
                      ],
                    },
                    {
                      num: "② 창작·기획",
                      roles: [
                        { label: "A&R 담당", tag: "고유", color: "#7C6FF7" },
                        { label: "공연기획 PD", tag: "고유", color: "#7C6FF7" },
                        { label: "사업팀장", tag: "고유", color: "#7C6FF7" },
                        { label: "에디터", tag: "고유", color: "#7C6FF7" },
                        { label: "크리에이티브 디렉터", tag: "고유", color: "#7C6FF7" },
                      ],
                    },
                    {
                      num: "③ 영업·운영",
                      roles: [
                        { label: "법무 담당", tag: "고유", color: "#6B9FF7" },
                        { label: "티켓영업 MD", tag: "고유", color: "#6B9FF7" },
                        { label: "후원개발팀", tag: "고유", color: "#6B9FF7" },
                        { label: "계약 MD", tag: "고유", color: "#6B9FF7" },
                        { label: "영업 AE", tag: "고유", color: "#6B9FF7" },
                      ],
                    },
                    {
                      num: "④ 마케팅",
                      roles: [
                        { label: "마케팅 담당", tag: "공통", color: "#5BB5F7" },
                        { label: "마케팅팀장", tag: "공통", color: "#5BB5F7" },
                        { label: "회계담당", tag: "고유", color: "#5BB5F7" },
                        { label: "마케터", tag: "공통", color: "#5BB5F7" },
                        { label: "영상 PD", tag: "고유", color: "#5BB5F7" },
                      ],
                    },
                    {
                      num: "⑤ 재무·분석",
                      roles: [
                        { label: "재무 담당", tag: "공통", color: "#4BBFF7" },
                        { label: "재무담당", tag: "공통", color: "#4BBFF7" },
                        { label: "사업평가위원", tag: "고유", color: "#4BBFF7" },
                        { label: "법무담당", tag: "고유", color: "#4BBFF7" },
                        { label: "데이터 분석가", tag: "고유", color: "#4BBFF7" },
                      ],
                    },
                  ].map((row, ri) => (
                    <tr key={ri} className={`border-b border-white/[0.04] ${ri % 2 === 0 ? "" : "bg-white/[0.01]"}`}>
                      <td className="py-3 px-4 text-[#3D3D3D] text-[10px] font-mono whitespace-nowrap">{row.num}</td>
                      {row.roles.map((role, ci) => (
                        <td key={ci} className="py-3 px-3">
                          <div
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium whitespace-nowrap"
                            style={{
                              background: `${role.color}12`,
                              border: `1px solid ${role.color}25`,
                              color: `${role.color}`,
                            }}
                          >
                            {role.label}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 border-t border-white/[0.04] flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#4B6CF7]/20 border border-[#4B6CF7]/30"></div>
                <span className="text-[#444444] text-[10px]">총괄(공통): 전 메뉴 접근·최종 결재권</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#7C6FF7]/15 border border-[#7C6FF7]/25"></div>
                <span className="text-[#444444] text-[10px]">창작·기획: 기업 전용 허브 담당</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#4BBFF7]/15 border border-[#4BBFF7]/25"></div>
                <span className="text-[#444444] text-[10px]">재무·분석: 재무 ERP 전담</span>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive Company Explorer */}
        <div className="w-full max-w-5xl mx-auto mb-10 px-6">
          <div className="bg-[#111112] border border-white/[0.07] rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-white/[0.05] flex items-center gap-2">
              <i className="ri-compass-discover-line text-[#7A9BFF] text-sm"></i>
              <span className="text-[#CCCCCC] text-sm font-semibold">기업 유형별 직무·메뉴 탐색기</span>
              <span className="ml-auto text-[#444444] text-xs hidden sm:block">기업 유형을 선택해 직무별 접근 메뉴를 확인하세요</span>
            </div>

            {/* Company Tabs */}
            <div className="flex overflow-x-auto border-b border-white/[0.05] scrollbar-none">
              {companyExplorer.map((co, i) => (
                <button
                  key={co.key}
                  type="button"
                  onClick={() => setActiveCompany(i)}
                  className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-all cursor-pointer shrink-0 border-b-2 ${
                    activeCompany === i
                      ? "border-[#4B6CF7] text-[#CCCCCC] bg-[#4B6CF7]/[0.05]"
                      : "border-transparent text-[#555555] hover:text-[#888888] hover:bg-white/[0.02]"
                  }`}
                >
                  <i className={`${co.icon} text-xs`} style={{ color: activeCompany === i ? co.color : undefined }}></i>
                  <span>{co.name}</span>
                </button>
              ))}
            </div>

            {/* Content Area */}
            {(() => {
              const co = companyExplorer[activeCompany];
              return (
                <div className="p-5">
                  {/* Company meta */}
                  <div className="flex items-center gap-3 mb-5">
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
                      style={{ background: `${co.color}15`, border: `1px solid ${co.color}25` }}
                    >
                      <i className={`${co.icon} text-base`} style={{ color: co.color }}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[#F2F2F2] text-sm font-semibold">{co.name}</span>
                        <span className="text-[#3D3D3D] text-[10px] uppercase tracking-wider">{co.en}</span>
                        <span className="text-[10px] text-[#555555] bg-white/[0.04] border border-white/[0.06] px-2 py-0.5 rounded-full">
                          초기 자본금 {co.capital}
                        </span>
                      </div>
                      <span className="text-[#4B6CF7]/70 text-[10px]">
                        전용 허브: {co.hubMenu}
                      </span>
                    </div>
                  </div>

                  {/* Role cards grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    {co.roles.map((role, ri) => (
                      <div
                        key={ri}
                        className="bg-[#0C0C0E] border border-white/[0.05] rounded-xl p-4 hover:border-white/[0.1] transition-all"
                      >
                        {/* Role header */}
                        <div className="flex items-center gap-2 mb-3">
                          <div
                            className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                            style={{ background: `${co.color}12`, border: `1px solid ${co.color}20` }}
                          >
                            <i className={`${role.icon} text-xs`} style={{ color: co.color }}></i>
                          </div>
                          <span
                            className="text-[11px] font-semibold leading-tight"
                            style={{ color: ri === 0 ? co.color : "#CCCCCC" }}
                          >
                            {role.label}
                          </span>
                        </div>

                        {/* Description */}
                        <p className="text-[#555555] text-[10px] leading-relaxed mb-3">{role.desc}</p>

                        {/* Accessible menus */}
                        <div>
                          <p className="text-[#555555] text-[9px] uppercase tracking-widest mb-1.5">접근 메뉴</p>
                          <div className="flex flex-wrap gap-1">
                            {role.menus.map((menu) => (
                              <span
                                key={menu}
                                className="text-[9px] px-1.5 py-0.5 rounded"
                                style={{
                                  background: menu === co.hubMenu ? `${co.color}15` : "rgba(255,255,255,0.03)",
                                  border: `1px solid ${menu === co.hubMenu ? `${co.color}30` : "rgba(255,255,255,0.06)"}`,
                                  color: menu === co.hubMenu ? co.color : "#444444",
                                }}
                              >
                                {menu}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

      {/* Role Toggle */}
        <div className="inline-flex bg-[#111112] border border-white/[0.08] rounded-full p-1 gap-1">
          {(["student", "instructor"] as Role[]).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRole(r)}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                role === r
                  ? "bg-[#4B6CF7] text-white"
                  : "text-[#666666] hover:text-[#AAAAAA]"
              }`}
            >
              {r === "student" ? (
                <><i className="ri-graduation-cap-line mr-1.5"></i>수강생 가이드</>
              ) : (
                <><i className="ri-user-star-line mr-1.5"></i>강사 가이드</>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <div className="space-y-6">
          {steps.map((step, index) => (
            <div
              key={step.step}
              className="bg-[#111112] border border-white/[0.07] rounded-2xl p-8 transition-all"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Step Number + Icon */}
                <div className="flex items-start gap-4 shrink-0">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0"
                    style={{ background: `${step.color}18`, border: `1px solid ${step.color}30` }}
                  >
                    <i className={`${step.icon} text-xl`} style={{ color: step.color }}></i>
                  </div>
                  <div className="lg:hidden">
                    <span className="text-[#555555] text-xs font-mono uppercase tracking-widest">STEP {step.step}</span>
                    <h2 className="text-[#F2F2F2] text-lg font-bold mt-0.5">{step.title}</h2>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="hidden lg:block mb-2">
                    <span className="text-[#555555] text-xs font-mono uppercase tracking-widest">STEP {step.step}</span>
                    <h2 className="text-[#F2F2F2] text-xl font-bold mt-0.5">{step.title}</h2>
                  </div>
                  <p className="text-[#888888] text-sm leading-relaxed mb-5">{step.description}</p>

                  <ul className="space-y-2 mb-5">
                    {step.details.map((detail) => (
                      <li key={detail} className="flex items-start gap-2.5 text-sm text-[#777777]">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                          <i className="ri-check-line text-[#4B6CF7] text-xs"></i>
                        </div>
                        {detail}
                      </li>
                    ))}
                  </ul>

                  {/* Tip */}
                  <div className="flex items-start gap-2.5 bg-[#4B6CF7]/[0.06] border border-[#4B6CF7]/15 rounded-xl px-4 py-3">
                    <i className="ri-lightbulb-line text-[#7A9BFF] text-sm mt-0.5 shrink-0"></i>
                    <p className="text-[#7A9BFF]/80 text-xs leading-relaxed">{step.tip}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Access Cards */}
        <div className="mt-14">
          <h3 className="text-[#F2F2F2] text-xl font-bold mb-6 text-center">바로 시작하기</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: "ri-login-box-line", title: "로그인", desc: "이미 계정이 있다면 바로 시작", path: "/login", color: "#4B6CF7" },
              { icon: "ri-question-answer-line", title: "자주 묻는 질문", desc: "궁금한 점 빠르게 해결", path: "/faq", color: "#7C6FF7" },
              { icon: "ri-mail-send-line", title: "도입 문의", desc: "기관 단체 도입 상담 신청", path: "/#contact", color: "#6B9FF7" },
            ].map((card) => (
              <button
                key={card.title}
                type="button"
                onClick={() => {
                  if (card.path.startsWith("/#")) {
                    navigate("/");
                    setTimeout(() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }), 300);
                  } else {
                    navigate(card.path);
                  }
                }}
                className="bg-[#111112] border border-white/[0.07] hover:border-white/[0.14] rounded-2xl p-6 text-left transition-all cursor-pointer group"
              >
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl mb-4"
                  style={{ background: `${card.color}18`, border: `1px solid ${card.color}30` }}
                >
                  <i className={`${card.icon} text-base`} style={{ color: card.color }}></i>
                </div>
                <p className="text-[#F2F2F2] font-semibold text-sm mb-1 group-hover:text-white transition-colors">{card.title}</p>
                <p className="text-[#555555] text-xs">{card.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-14">
          <h3 className="text-[#F2F2F2] text-xl font-bold mb-2 text-center">자주 묻는 질문</h3>
          <p className="text-[#555555] text-sm text-center mb-8">가이드를 읽고도 궁금한 점이 있으시면 아래를 확인해보세요.</p>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-[#111112] border border-white/[0.07] rounded-xl overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer group"
                >
                  <span className="text-[#CCCCCC] text-sm font-medium group-hover:text-[#F2F2F2] transition-colors pr-4">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-[#555555] text-lg shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 border-t border-white/[0.05]">
                    <p className="text-[#777777] text-sm leading-relaxed pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-14 bg-[#111112] border border-white/[0.07] rounded-2xl p-8 text-center">
          <i className="ri-customer-service-2-line text-[#7A9BFF] text-3xl mb-4 block"></i>
          <h3 className="text-[#F2F2F2] text-lg font-bold mb-2">추가 도움이 필요하신가요?</h3>
          <p className="text-[#666666] text-sm mb-6 leading-relaxed">
            가이드에 없는 내용이나 기관 도입 상담은<br />이메일로 문의해 주세요.
          </p>
          <a
            href="mailto:eunhyun1@naver.com"
            className="inline-flex items-center gap-2 bg-[#4B6CF7] hover:bg-[#5B7CF7] text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-line"></i>
            eunhyun1@naver.com
          </a>
        </div>
      </section>

      {/* Footer */}
      <div className="border-t border-white/[0.05] py-6 text-center">
        <p className="text-[#555555] text-xs">© 2026 주식회사 툴뮤직. All rights reserved.</p>
      </div>
    </div>
  );
}
